/**
 * Tele Sale API — calls /api/telesales/* with the signed-in user's token.
 * Every permission is checked by the server; the portal only hides what a
 * role can't use.
 */
import axios from 'axios';

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/telesales`;

function headers() {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function errorMessage(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail?.errors)) return detail.errors.join(' ');
  if (Array.isArray(detail)) return detail.map(d => d.msg).join(', ');
  return err?.message || 'Something went wrong';
}

async function call(method, path, data, params) {
  try {
    const res = await axios({ method, url: `${BASE}${path}`, data, params, headers: headers() });
    return res.data;
  } catch (err) {
    if (err?.response?.status === 401) {
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }
    throw new Error(errorMessage(err));
  }
}

const get = (p, params) => call('get', p, undefined, params);
const post = (p, d) => call('post', p, d || {});
const put = (p, d) => call('put', p, d || {});
const patch = (p, d) => call('patch', p, d || {});
const del = (p, params) => call('delete', p, undefined, params);

export const ts = {
  catalog: () => get('/catalog'),
  me: () => get('/me'),
  updateMe: (body) => put('/me', body),
  updateCompany: (body) => put('/company', body),
  updateSection: (section, body) => put(`/company/${section}`, body),
  phoneMenu: () => get('/phone-menu'),
  savePhoneMenu: (body) => put('/phone-menu', body),
  previewPhoneMenu: (body) => post('/phone-menu/preview', body),

  setSeats: (seats) => post('/setup/seats', { seats }),
  setPlan: (plan) => post('/setup/plan', { plan }),
  searchNumbers: (params) => get('/numbers/search', params),
  cart: () => get('/cart'),
  cartAdd: (items) => post('/cart/add', { items }),
  cartRemove: (numbers) => post('/cart/remove', { numbers }),
  cartMain: (item) => post('/cart/main', { item }),
  website: () => get('/company/website'),
  relearnWebsite: () => post('/company/website/learn'),
  checkout: () => post('/checkout'),
  confirmCheckout: () => post('/checkout/confirm'),
  provisioning: () => get('/provisioning'),
  retryProvisioning: () => post('/provisioning/retry'),
  repick: (numberId, e164) => post(`/numbers/${numberId}/repick`, { e164 }),
  numbers: () => get('/numbers'),
  updateNumber: (id, body) => patch(`/numbers/${id}`, body),
  avatarVoices: () => get('/numbers/voices'),
  cancelAddSeats: () => post('/billing/seats/add/cancel'),

  brainFiles: () => get('/brain/files'),
  uploadBrainFile: async (file) => {
    const form = new FormData();
    form.append('file', file);
    return call('post', '/brain/files', form);
  },
  deleteBrainFile: (id) => del(`/brain/files/${id}`),
  buildBrain: () => post('/brain/build'),
  brainInterview: () => get('/brain/interview'),
  draftBrain: (answers) => post('/brain/interview/draft', { answers }),
  trainBrain: (items) => post('/brain/interview/train', { items }),

  smsRegistration: () => get('/sms/registration'),
  submitSmsRegistration: (body) => post('/sms/registration', body),

  staff: () => get('/staff'),
  addStaff: (body) => post('/staff', body),
  assignNumber: (memberId, numberId) => post(`/staff/${memberId}/assign`, { number_id: numberId }),
  unassignNumber: (memberId) => post(`/staff/${memberId}/unassign`),
  staffAction: (memberId, action) => post(`/staff/${memberId}/${action}`),
  changeRole: (memberId, role) => put(`/staff/${memberId}/role`, { role }),
  staffActivity: (memberId) => get(`/staff/${memberId}/activity`),
  setUsername: (memberId, username) => put(`/staff/${memberId}/username`, { username }),
  uploadPhoto: async (memberId, file) => {
    const form = new FormData();
    form.append('file', file);
    return call('put', `/staff/${memberId}/photo`, form);
  },
  removePhoto: (memberId) => del(`/staff/${memberId}/photo`),
  // The photo needs the sign-in token, so it is fetched as a blob, not an <img src>.
  photoBlob: async (memberId, version) => {
    const res = await axios.get(`${BASE}/staff/${memberId}/photo`, { params: { v: version }, headers: headers(), responseType: 'blob' });
    return res.data;
  },

  placeCall: (body) => post('/calls', body),
  calls: (scope = 'mine') => get('/calls', { scope }),
  appCalls: () => get('/rtc/history'),
  disposition: (callId, disposition, notes = '') => post(`/calls/${callId}/disposition`, { disposition, notes }),
  sendSms: (to, body) => post('/sms', { to, body }),
  conversations: () => get('/conversations'),
  conversation: (id) => get(`/conversations/${id}`),

  leads: (params) => get('/leads', params),
  createLead: (body) => post('/leads', body),
  importLeads: async (file) => {
    const form = new FormData();
    form.append('file', file);
    return call('post', '/leads/import', form);
  },
  pipeline: () => get('/pipeline'),
  lead: (id) => get(`/leads/${id}`),
  updateLead: (id, body) => patch(`/leads/${id}`, body),
  assignLead: (id, memberId) => post(`/leads/${id}/assign`, { member_id: memberId }),
  addNote: (id, text) => post(`/leads/${id}/notes`, { text }),

  callbacks: (scope = 'mine', status = 'open') => get('/callbacks', { scope, status }),
  createCallback: (body) => post('/callbacks', body),
  updateCallback: (id, body) => patch(`/callbacks/${id}`, body),
  reminders: () => get('/reminders'),

  campaigns: () => get('/campaigns'),
  createCampaign: (body) => post('/campaigns', body),
  campaign: (id) => get(`/campaigns/${id}`),
  campaignStatus: (id, status) => post(`/campaigns/${id}/status`, { status }),
  campaignNext: (id) => get(`/campaigns/${id}/next`),
  campaignResult: (id, body) => post(`/campaigns/${id}/result`, body),

  aiAsk: (question) => post('/ai/ask', { question }),
  aiTools: () => get('/ai/tools'),
  aiRun: (tool, args = {}, confirmed = false) => post('/ai/run', { tool, args, confirmed }),
  aiApprove: (id) => post(`/ai/actions/${id}/approve`),
  aiReject: (id) => post(`/ai/actions/${id}/reject`),
  aiActions: () => get('/ai/actions'),

  dashboard: () => get('/dashboard'),
  supervisor: (days = 1) => get('/supervisor', { days }),
  coaching: (body) => post('/supervisor/coaching', body),
  analytics: (days = 30) => get('/analytics', { days }),
  billing: () => get('/billing'),
  changePlan: (plan) => post('/billing/plan', { plan }),
  addSeats: (additional) => post('/billing/seats/add', { additional }),
  reduceSeats: (numberIds) => post('/billing/seats/reduce', { number_ids: numberIds }),
  cancel: (reason) => post('/billing/cancel', { reason }),

  // Staff communications app (owner plan v2, 2026-11-01)
  control: () => get('/control'),
  issueCode: (memberId) => post(`/control/members/${memberId}/activation-code`),
  revokeCodes: (memberId) => del(`/control/members/${memberId}/activation-code`),
  memberDevices: (memberId) => get(`/control/members/${memberId}/devices`),
  revokeDevice: (memberId, deviceId) => del(`/control/members/${memberId}/devices/${deviceId}`),
  forceLogout: (memberId) => post(`/control/members/${memberId}/force-logout`),
  setStatus: (memberId, action) => post(`/control/members/${memberId}/status/${action}`),
  updateMember: (memberId, body) => patch(`/control/members/${memberId}`, body),
  transferNumber: (memberId, toMemberId) => post(`/control/members/${memberId}/transfer`, { to_member_id: toMemberId }),
  staffAppSettings: (body) => put('/control/settings', body),
  setMemberStatus: (id, body) => put(`/control/members/${id}/status`, body),
  supportReports: () => get('/support/reports'),
  closeSupportReport: (id) => post(`/support/reports/${id}/close`),
  commsChats: (params) => get('/comms/chats', params),
  commsChat: (id) => get(`/comms/chats/${id}`),
  commsImage: async (id) => {
    const res = await axios.get(`${BASE}/comms/images/${id}`, { headers: headers(), responseType: 'blob' });
    return res.data;
  },
  commsSearch: (params) => get('/comms/search', params),
  commsSms: (params) => get('/comms/sms', params),
  commsSmsThread: (id) => get(`/comms/sms/${id}`),
  commsCalls: (params) => get('/comms/calls', params),
  commsExport: async (params) => {
    const res = await axios.get(`${BASE}/comms/export`, { params, headers: headers(), responseType: 'blob' });
    return res.data;
  },
  commsRetention: () => get('/comms/retention'),
  setCommsRetention: (chatDays) => put('/comms/retention', { chat_days: chatDays }),
  commsAccessLog: () => get('/comms/access-log'),

  suppression: () => get('/compliance/suppression'),
  suppress: (body) => post('/compliance/suppression', body),
  unsuppress: (phone, channel) => del('/compliance/suppression', { phone, channel }),
  audit: () => get('/audit'),
};

// Permission names, mirroring backend telesales.config
export const P = {
  COMPANY: 'company.manage',
  BILLING: 'billing.manage',
  STAFF: 'staff.manage',
  NUMBERS: 'numbers.assign',
  SUPERVISE: 'supervise',
  LEADS_ALL: 'leads.all',
  LEADS_ASSIGN: 'leads.assign',
  CALL: 'phone.call',
  SMS: 'phone.sms',
  CAMPAIGNS: 'campaigns.manage',
  AI: 'ai.run',
  COMPLIANCE: 'compliance.manage',
  CHAT: 'chat.use',
  COMMS: 'comms.review',
  COMMS_EXPORT: 'comms.export',
};

// Plan features, mirroring backend telesales.config (the server enforces them)
export const F = {
  CAMPAIGNS: 'campaigns',
  CALLBACK_CENTER: 'callback_center',
  CALLBACK_ADVANCED: 'callback_advanced',
  AI_FRONT_DESK: 'ai_front_desk',
  AI_BRAIN: 'ai_brain',
  ANALYTICS: 'analytics',
  SUPERVISOR: 'supervisor',
  ANALYTICS_ADVANCED: 'analytics_advanced',
};
